import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, TextField, IconButton, Button, Chip, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchData } from "../../api/client";
import { getConversationById } from "../../api/client";
import { useRequests } from "../../hooks/useRequests";
import StatusChipSelector, { normalizeStatusInput } from "../StatusChipSelector";

export default function ChatbotSimulator({ userId }) {
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 32, y: 80 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [requestStatus, setRequestStatus] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const { updateMutation } = useRequests();

  // Ao carregar a tela, posiciona o simulador minimizado no canto inferior direito
  const computeInitialPosition = () => {
    try {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      const width = 320;
      const height = 60; // altura aproximada do cabeçalho minimizado
      const margin = 16;
      // Posição inicial: parte inferior central da área útil,
      // para não conflitar com elementos laterais do canvas.
      const x = Math.max(margin, (w - width) / 2);
      const y = Math.max(64, h - height - margin);
      return { x, y };
    } catch {
      return { x: 32, y: 80 };
    }
  };

  useEffect(() => {
    const pos = computeInitialPosition();
    setPosition(pos);
  }, []);

  const protocol = useMemo(() => (sessionId ? String(sessionId) : ""), [sessionId]);
  const isClosedRequest = useMemo(() => {
    const s = normalizeStatusInput(requestStatus);
    return s === 2 || s === 4 || s === 5;
  }, [requestStatus]);

  const refreshRequestDetails = async (id) => {
    if (!id) return;
    try {
      setLoadingRequest(true);
      const dto = await getConversationById(id);
      const status = dto?.status ?? dto?.data?.status ?? null;
      if (status !== null && status !== undefined) {
        setRequestStatus(normalizeStatusInput(status));
      }
    } catch (err) {
      // não trava o simulador se falhar a leitura do status
      console.error("[ChatbotSimulator] Erro ao buscar detalhes da solicitação:", err);
    } finally {
      setLoadingRequest(false);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setRequestStatus(null);
      return;
    }
    refreshRequestDetails(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const openPopupCall = () => {
    if (!sessionId) return;
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${sessionId}`,
      "SolicitacaoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  const handleStatusChange = (newStatus) => {
    if (!sessionId) return;
    updateMutation.mutate(
      { id: sessionId, status: newStatus },
      {
        onSuccess: async () => {
          setRequestStatus(normalizeStatusInput(newStatus));
          await refreshRequestDetails(sessionId);
        },
        onError: (err) => {
          console.error("[ChatbotSimulator] Erro ao atualizar status:", err);
          const msg =
            err?.message ||
            err?.error ||
            (Array.isArray(err?.message) && err.message[0]) ||
            "Erro ao atualizar status da solicitação.";
          setMessages((prev) => [...prev, { from: "system", text: msg }]);
        },
      }
    );
  };

  const listMessageToLabels = (listMessage) => {
    if (!listMessage) return [];
    if (Array.isArray(listMessage)) {
      return listMessage
        .map((x) => {
          if (typeof x === "string") return x;
          if (x && typeof x === "object") return x.title || x.nome || x.label || x.text || x.name || "";
          return "";
        })
        .map((s) => String(s).trim())
        .filter(Boolean);
    }
    if (typeof listMessage === "object" && Array.isArray(listMessage.sections)) {
      const out = [];
      for (const sec of listMessage.sections) {
        const rows = Array.isArray(sec.rows) ? sec.rows : [];
        for (const r of rows) {
          const t = r?.title || r?.nome || r?.label || "";
          if (t) out.push(String(t).trim());
        }
      }
      return out;
    }
    return [];
  };

  const parseQuickReplies = (payload) => {
    const p = payload ?? {};
    const raw =
      p.buttonMessage ??
      p.botoes ??
      p.buttons ??
      p.quickReplies ??
      p.quick_replies ??
      null;

    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(",")
        .map((s) => String(s).trim())
        .filter(Boolean);
    }

    if (Array.isArray(raw) && raw.length) {
      return raw
        .map((x) => {
          if (typeof x === "string") return x;
          if (x && typeof x === "object") return x.label || x.title || x.text || x.name || "";
          return "";
        })
        .map((s) => String(s).trim())
        .filter(Boolean);
    }

    const fromList = listMessageToLabels(p.listMessage);
    if (fromList.length) return fromList;

    return [];
  };

  const sendMessage = async (text, opts = {}) => {
    const { clearButtonsFromMessageIdx } = opts || {};
    const trimmed = String(text || "").trim();
    if (!trimmed || !userId) return;
    if (isClosedRequest) return;

    // Comportamento WhatsApp: ao escolher uma opção, os botões daquela resposta "somem"
    if (typeof clearButtonsFromMessageIdx === "number") {
      setMessages((prev) =>
        prev.map((m, i) => (i === clearButtonsFromMessageIdx ? { ...m, buttons: [] } : m))
      );
    }

    setMessages((prev) => [...prev, { from: "user", text: trimmed }]);

    try {
      setSending(true);
      let sid = sessionId;
      if (!sid) {
        const res = await fetchData("/chatbot/sessions", "POST", { user_id: userId });
        const data = res?.data ?? res ?? {};
        sid = data.sessionId;
        setSessionId(sid);
        await refreshRequestDetails(sid);
      }

      const resMsg = await fetchData(`/chatbot/sessions/${sid}/messages`, "POST", { message: trimmed });
      const dataMsg = resMsg?.data ?? resMsg ?? {};
      const botText = dataMsg.mensagem || dataMsg.retorno || "";
      const buttons = parseQuickReplies(dataMsg);

      if (botText) {
        setMessages((prev) => [
          ...prev,
          { from: "bot", text: botText, buttons },
        ]);
      } else if (buttons.length) {
        // Se por algum motivo vier apenas botão sem texto, ainda assim exibir no simulador.
        setMessages((prev) => [
          ...prev,
          { from: "bot", text: "", buttons },
        ]);
      }
    } catch (err) {
      const msg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        "Erro ao simular mensagem.";
      setMessages((prev) => [...prev, { from: "system", text: msg }]);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !userId) return;
    setInput("");
    await sendMessage(text);
  };

  const handleQuickReplyClick = async (replyText, messageIdx) => {
    if (!replyText) return;
    if (!userId || sending || isClosedRequest) return;
    await sendMessage(replyText, { clearButtonsFromMessageIdx: messageIdx });
  };

  const handleReset = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setRequestStatus(null);
  };

  const handleMouseDown = (e) => {
    // Evita iniciar drag ao clicar em botões/inputs
    if (e.target instanceof HTMLElement && e.target.closest("button, input, textarea")) {
      return;
    }
    e.preventDefault();
    setDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleDragMove = (clientX, clientY) => {
    setPosition({
      x: Math.max(8, clientX - offset.x),
      y: Math.max(64, clientY - offset.y),
    });
  };

  // listeners globais para não "perder" o drag quando o mouse sai do Paper
  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e) => {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    };

    const onUp = () => {
      setDragging(false);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, offset.x, offset.y]);

  return (
    <Paper
      elevation={1}
      sx={{
        position: "fixed",
        // Mantém o simulador acima do conteúdo, mas abaixo de Dialogs/Drawers (z-index padrão 1300),
        // para que, ao abrir popups, ele também fique escurecido pelo backdrop.
        zIndex: 1100,
        top: position.y,
        right: "auto",
        left: position.x,
        p: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        width: 320,
        maxWidth: "90vw",
        height: expanded ? 420 : "auto",
        maxHeight: 480,
        cursor: dragging ? "grabbing" : "default",
        boxShadow: (t) => t.shadows[12],
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          cursor: "grab",
          userSelect: dragging ? "none" : "auto",
          gap: 1,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Teste o ChatBot
        </Typography>
        <Button
          size="small"
          onClick={handleReset}
          sx={{ ml: "auto", textTransform: "none" }}
          disabled={!messages.length && !sessionId}
        >
          Limpar
        </Button>
        <Button
          size="small"
          onClick={() => {
            setExpanded((prev) => {
              const next = !prev;
              if (!prev && next) {
                // Ao expandir pela primeira vez, garante que o popup caiba inteiro na tela,
                // abrindo visualmente "para cima", mas sem cortar.
                try {
                  const h = window.innerHeight || 0;
                  const popupHeight = 420; // mesma altura usada no sx
                  const margin = 16;
                  const targetY = Math.max(64, h - popupHeight - margin);
                  setPosition((p) => ({
                    ...p,
                    y: targetY,
                  }));
                } catch {
                  // ignore em ambientes sem window
                }
              } else if (prev && !next) {
                // Ao minimizar, volta para a posição inicial calculada
                const initial = computeInitialPosition();
                setPosition(initial);
              }
              return next;
            });
          }}
          sx={{ ml: 1, textTransform: "none" }}
        >
          {expanded ? "Minimizar" : "Expandir"}
        </Button>
      </Box>
      {expanded && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              px: 0.5,
            }}
          >
            {!sessionId ? (
              <Typography variant="body2" color="text.secondary">
                Ao iniciar o teste, o simulador cria automaticamente uma <b>solicitação</b> (simulação) para este ChatBot.
                Depois de criada, você verá aqui o <b>protocolo</b> e poderá abrir em um <b>popup</b> e alterar o <b>status</b>.
              </Typography>
            ) : (
              <>
                <Tooltip title={`Abrir solicitação ${protocol} em popup`}>
                  <Chip
                    clickable
                    onClick={openPopupCall}
                    label={`Protocolo: ${protocol}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      maxWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Tooltip>

                <IconButton
                  size="small"
                  onClick={openPopupCall}
                  title="Abrir solicitação em popup"
                  sx={{ ml: -0.5 }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>

                {requestStatus !== null && requestStatus !== undefined && (
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <StatusChipSelector
                      currentStatus={requestStatus}
                      onStatusChange={handleStatusChange}
                      size="small"
                    />
                    {loadingRequest && (
                      <Typography variant="caption" color="text.secondary">
                        Atualizando…
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              mb: 1,
              px: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
            }}
          >
            {messages.map((m, idx) => {
              const alignment = m.from === "user" ? "flex-end" : "flex-start";
              const hasButtons = Array.isArray(m.buttons) && m.buttons.length > 0;

              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: alignment,
                    mt: 0.5,
                  }}
                >
                  {(m.text != null && String(m.text).trim() !== "") && (
                    <Box
                      sx={{
                        maxWidth: "78%",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor:
                          m.from === "user"
                            ? "primary.main"
                            : m.from === "bot"
                            ? "grey.200"
                            : "warning.light",
                        color: m.from === "user" ? "primary.contrastText" : "text.primary",
                        fontSize: "0.85rem",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {m.text}
                    </Box>
                  )}

                  {/* Quick replies (estilo WhatsApp): ficam logo abaixo da mensagem do bot */}
                  {m.from === "bot" && hasButtons && (
                    <Box
                      sx={{
                        width: "78%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mt: m.text ? 0.5 : 0,
                      }}
                    >
                      {m.buttons.map((b, bIdx) => (
                        <Button
                          key={`${idx}-${bIdx}-${b}`}
                          variant="outlined"
                          size="small"
                          onClick={() => handleQuickReplyClick(b, idx)}
                          disabled={sending || isClosedRequest}
                          sx={{
                            textTransform: "none",
                            justifyContent: "center",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          {b}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Digite uma mensagem abaixo para testar o fluxo do ChatBot atribuído a este usuário. As respostas usam o
                mesmo fluxo aplicado às conversas reais.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <TextField
              size="small"
              placeholder={
                isClosedRequest
                  ? "Solicitação encerrada — não é possível enviar mensagens."
                  : "Digite uma mensagem..."
              }
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!userId || sending || isClosedRequest}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isClosedRequest) handleSend();
                }
              }}
            />
            <IconButton
              sx={{ ml: 1 }}
              color="primary"
              onClick={handleSend}
              disabled={!userId || sending || isClosedRequest || !input.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </>
      )}
    </Paper>
  );
}

